import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Camera, RefreshCw, Check, X, Loader2 } from "lucide-react";
import { loadModels, detectDescriptor } from "@/lib/faceApi";
import { toast } from "sonner";

// Reusable camera modal. mode="verify" -> onCapture(descriptor). mode="enroll" -> onEnroll(descriptors[]).
// Camera stream is started only while open and stopped on success/cancel/unmount (no background use).
export default function FaceCapture({ open, mode = "verify", samplesNeeded = 3, title, subject, onCapture, onEnroll, onClose }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [phase, setPhase] = useState("init"); // init | ready | error
  const [error, setError] = useState("");
  const [samples, setSamples] = useState([]);
  const [busy, setBusy] = useState(false);

  const stop = () => {
    if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
  };

  const start = async () => {
    setError(""); setPhase("init");
    try {
      if (!navigator.mediaDevices?.getUserMedia) { setError("Camera is not supported on this browser/device."); setPhase("error"); return; }
      await loadModels();
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: { ideal: 480 }, height: { ideal: 480 } }, audio: false });
      streamRef.current = stream;
      if (videoRef.current) { videoRef.current.srcObject = stream; await videoRef.current.play(); }
      setPhase("ready");
    } catch (e) {
      setError(e?.name === "NotAllowedError" ? "Camera permission denied. Please allow camera access and try again."
        : e?.name === "NotFoundError" ? "No camera found on this device."
        : "Could not start the camera. " + (e?.message || ""));
      setPhase("error");
    }
  };

  useEffect(() => {
    if (!open) { stop(); return; }
    setSamples([]); start();
    return stop;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const grab = async () => {
    if (!videoRef.current) return null;
    const r = await detectDescriptor(videoRef.current);
    if (r.error) {
      toast.error({ no_face: "No face detected — center your face in the circle.",
        multiple: "Multiple faces detected — only one person at a time.",
        low_quality: "Face not clear — improve lighting and hold steady." }[r.error] || "Could not read face.");
      return null;
    }
    return r.descriptor;
  };

  const doVerify = async () => { setBusy(true); const d = await grab(); setBusy(false); if (d) { stop(); onCapture?.(d); } };
  const doSample = async () => {
    setBusy(true); const d = await grab(); setBusy(false);
    if (!d) return;
    const next = [...samples, d];
    setSamples(next);
    if (next.length >= samplesNeeded) { stop(); onEnroll?.(next); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { stop(); onClose?.(); } }}>
      <DialogContent className="max-w-sm" data-testid="face-capture-dialog">
        <DialogHeader>
          <DialogTitle>{title || (mode === "enroll" ? "Face Enrollment" : "Face Verification")}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col items-center gap-3">
          {subject && <div className="text-xs font-mono text-slate-500" data-testid="face-subject">{subject}</div>}
          <div className="relative w-56 h-56 max-w-full rounded-full overflow-hidden border-4 border-sky-400 bg-slate-900">
            <video ref={videoRef} playsInline muted className="w-full h-full object-cover" data-testid="face-video" />
            {phase !== "ready" && (
              <div className="absolute inset-0 grid place-items-center text-white text-xs text-center px-4">
                {phase === "error" ? <span className="text-rose-300" data-testid="face-error">{error}</span> : <Loader2 className="w-6 h-6 animate-spin" />}
              </div>
            )}
          </div>
          <p className="text-xs text-slate-500 text-center">
            {mode === "enroll"
              ? `Capture ${samplesNeeded} samples — look straight, then slightly left and right. (${samples.length}/${samplesNeeded})`
              : "Center your face in the circle and tap Verify."}
          </p>
          {phase === "error" ? (
            <Button variant="outline" onClick={start} data-testid="face-retry-btn"><RefreshCw className="w-4 h-4 mr-2" />Try Again</Button>
          ) : mode === "enroll" ? (
            <Button disabled={busy || phase !== "ready"} onClick={doSample} className="w-full h-12 bg-sky-600 hover:bg-sky-700" data-testid="face-capture-sample-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Camera className="w-4 h-4 mr-2" />Capture Sample ({samples.length}/{samplesNeeded})</>}
            </Button>
          ) : (
            <Button disabled={busy || phase !== "ready"} onClick={doVerify} className="w-full h-12 bg-emerald-600 hover:bg-emerald-700" data-testid="face-verify-capture-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 mr-2" />Verify Face</>}
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={() => { stop(); onClose?.(); }} data-testid="face-cancel-btn"><X className="w-4 h-4 mr-1" />Cancel</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
